import { useTranslation } from "react-i18next";
import { AvatarPortrait } from "../world/humans/AvatarPortrait.js";
import {
  HAIR_COLORS,
  HAIR_STYLES,
  OUTFITS,
  OUTFIT_COLORS,
  SKIN_TONES,
  hairOptions,
  lookOptions,
  normalizeSpec,
} from "../world/humans/spec.js";
import { ROCKETBOX_IDS } from "../world/humans/rocketbox/looks.js";
import type { AvatarSpec } from "../world/humans/spec.js";

function Swatches({
  label,
  colors,
  value,
  onPick,
}: {
  label: string;
  colors: readonly string[];
  value: number;
  onPick: (i: number) => void;
}) {
  return (
    <div className="field">
      <span>{label}</span>
      <div className="avatar-swatches" role="group" aria-label={label}>
        {colors.map((c, i) => (
          <button
            key={c}
            type="button"
            className="avatar-swatch"
            style={{ background: c }}
            aria-pressed={value === i}
            aria-label={`${label} ${i + 1}`}
            onClick={() => onPick(i)}
          />
        ))}
      </div>
    </div>
  );
}

/** Label of an office look: "Business 2", "Office 1"... numbered within its kind and body. */
function lookLabel(t: (k: string, o?: Record<string, unknown>) => string, index: number): string {
  const id = ROCKETBOX_IDS[index]!;
  const m = /^(business|office)-[fm](\d+)$/.exec(id);
  return m ? t(`avatar.look.${m[1]}`, { n: Number(m[2]) }) : id;
}

/**
 * Picks a character over a live 3D portrait: the body, then either an office person (the default:
 * the Rocketbox people in business or smart office wear) or the classic body with skin tone,
 * hairstyle and color, outfit and color. Controlled; the owner decides when to save.
 */
export function AvatarCreator({
  spec,
  onChange,
  label,
  styles = true,
}: {
  spec: AvatarSpec;
  onChange: (next: AvatarSpec) => void;
  /** Name for the portrait's accessible label. */
  label: string;
  /** Offer the office people (false: the classic body only, e.g. the assistant's look). */
  styles?: boolean;
}) {
  const { t } = useTranslation("web");
  const set = (patch: Partial<AvatarSpec>) => onChange(normalizeSpec({ ...spec, ...patch }));
  return (
    <div className="avatar-creator" data-testid="avatar-creator">
      <AvatarPortrait
        spec={spec}
        label={t("avatar.portrait", { name: label })}
        framing="full"
        animate
      />
      <div className="avatar-creator-controls">
        <div className="field">
          <span>{t("avatar.body")}</span>
          <div className="avatar-chips" role="group" aria-label={t("avatar.body")}>
            {(["male", "female"] as const).map((b) => (
              <button
                key={b}
                type="button"
                className="avatar-chip"
                aria-pressed={spec.body === b}
                data-testid={`avatar-body-${b}`}
                onClick={() => set({ body: b })}
              >
                {t(`avatar.${b}`)}
              </button>
            ))}
          </div>
        </div>
        {styles ? (
          <div className="field">
            <span>{t("avatar.style")}</span>
            <div className="avatar-chips" role="group" aria-label={t("avatar.style")}>
              {(["office", "classic"] as const).map((st) => (
                <button
                  key={st}
                  type="button"
                  className="avatar-chip"
                  aria-pressed={spec.style === st}
                  data-testid={`avatar-style-${st}`}
                  onClick={() => set({ style: st })}
                >
                  {t(`avatar.styles.${st}`)}
                </button>
              ))}
            </div>
          </div>
        ) : null}
        {styles && spec.style === "office" ? (
          <div className="field">
            <span>{t("avatar.person")}</span>
            <div className="avatar-chips" role="group" aria-label={t("avatar.person")}>
              {lookOptions(spec.body).map((i) => (
                <button
                  key={i}
                  type="button"
                  className="avatar-chip"
                  aria-pressed={spec.look === i}
                  data-testid={`avatar-look-${ROCKETBOX_IDS[i]}`}
                  onClick={() => set({ look: i })}
                >
                  {lookLabel(t, i)}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <ClassicControls spec={spec} set={set} />
        )}
      </div>
    </div>
  );
}

function ClassicControls({
  spec,
  set,
}: {
  spec: AvatarSpec;
  set: (patch: Partial<AvatarSpec>) => void;
}) {
  const { t } = useTranslation("web");
  return (
    <>
      <Swatches
        label={t("avatar.skin")}
        colors={SKIN_TONES}
        value={spec.skin}
        onPick={(skin) => set({ skin })}
      />
      <div className="field">
        <span>{t("avatar.hair")}</span>
        <div className="avatar-chips" role="group" aria-label={t("avatar.hair")}>
          {hairOptions(spec.body).map((i) => (
            <button
              key={i}
              type="button"
              className="avatar-chip"
              aria-pressed={spec.hair === i}
              onClick={() => set({ hair: i })}
            >
              {t(`avatar.hairStyle.${HAIR_STYLES[i]!.id}`)}
            </button>
          ))}
        </div>
      </div>
      <Swatches
        label={t("avatar.hairColor")}
        colors={HAIR_COLORS}
        value={spec.hairColor}
        onPick={(hairColor) => set({ hairColor })}
      />
      <div className="field">
        <span>{t("avatar.outfit")}</span>
        <div className="avatar-chips" role="group" aria-label={t("avatar.outfit")}>
          {OUTFITS.map((o, i) => (
            <button
              key={o.id}
              type="button"
              className="avatar-chip"
              aria-pressed={spec.outfit === i}
              onClick={() => set({ outfit: i })}
            >
              {t(`avatar.outfits.${o.id}`)}
            </button>
          ))}
        </div>
      </div>
      <Swatches
        label={t("avatar.outfitColor")}
        colors={OUTFIT_COLORS}
        value={spec.outfitColor}
        onPick={(outfitColor) => set({ outfitColor })}
      />
    </>
  );
}
